import { useRef, ReactNode, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useHover } from '@react-aria/interactions';
import { useLink } from '@react-aria/link';
import { colors, s16, s4, s8 } from '@ardoq/design-tokens';
import { Icon, IconName } from '@ardoq/icons';
import { focusRingCSS } from '@ardoq/style-helpers';
import { text1 } from '@ardoq/typography';
import { Item } from './atoms';

const MenuItemLabel = styled.span<{
  $isHighlighted?: boolean;
}>`
  /*
    Despite these being -webkit, this is actually standardised
  */
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;

  color: ${({ $isHighlighted }) =>
    $isHighlighted ? colors.textDefault : colors.textSubtle};
`;

const MenuItemIcon = styled(Icon)<{
  $iconColor?: string;
}>`
  ${({ $iconColor }) =>
    $iconColor &&
    css`
      color: ${$iconColor};
    `}
`;

const MenuItemStatus = ({
  iconName,
  iconColor,
}: {
  iconName?: IconName;
  iconColor?: string;
}) => {
  return (
    <>
      {iconName && <MenuItemIcon iconName={iconName} $iconColor={iconColor} />}
    </>
  );
};

const MenuItem = styled.div<{
  $isSelected?: boolean;
  $isHighlighted?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: ${s8};
  padding: ${s8} ${s16};

  > ${MenuItemLabel} {
    flex: 1;
  }

  ${text1}
  cursor: pointer;
  background-color: ${({ $isSelected, $isHighlighted }) =>
    $isSelected || $isHighlighted
      ? colors.surfaceDefault
      : colors.surfaceStatusNeutral};

  ${({ $isSelected }) =>
    $isSelected &&
    css`
      padding-left: calc(${s16} - ${s4});
      border-left: ${s4} solid ${colors.surfaceActionBrandFocused};
    `}

  /*
    Apply the Ardoq-style focus ring
  */
  position: relative;
  &:focus {
    outline: none;
    background-color: ${colors.surfaceDefault};
    ${focusRingCSS}
    /*
      We must move the ring inside the component, because it cannot extend
      beyond the bounds of the scrollable sidebar area
    */
    &::before {
      top: 3px;
      bottom: 3px;
      left: ${({ $isSelected }) => ($isSelected ? '5px' : '9px')};
      right: 3px;
    }
  }
`;

type SidebarMenuItemProps = {
  label: string;
  statusIconName?: IconName;
  statusIconColor?: string;
  isSelected?: boolean;
  isFocused?: boolean;
  onPress?: VoidFunction;
};

export const SidebarMenuItem = ({
  label,
  statusIconName,
  statusIconColor,
  isSelected,
  isFocused,
  onPress,
  ...rest
}: SidebarMenuItemProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { linkProps, isPressed } = useLink(
    {
      elementType: 'div',
      onPress: onPress,
    },
    ref
  );
  const { hoverProps, isHovered } = useHover({});
  useEffect(() => {
    if (isFocused && ref.current) {
      ref.current.focus();
    }
  }, [isFocused]);
  return (
    <MenuItem
      $isSelected={isSelected}
      $isHighlighted={isPressed || isHovered}
      ref={ref}
      {...linkProps}
      {...hoverProps}
      {...rest}
    >
      <MenuItemLabel $isHighlighted={isSelected || isPressed || isHovered}>
        {label}
      </MenuItemLabel>
      <MenuItemStatus iconName={statusIconName} iconColor={statusIconColor} />
    </MenuItem>
  );
};

const RestyledItem = styled(Item)`
  flex-basis: auto;
  padding: 0;
  background-color: ${colors.surfaceStatusNeutral};
`;

type SidebarMenuProps = {
  navigationSubject: string;
  children: ReactNode;
};
export const SidebarMenu = ({
  navigationSubject,
  children,
}: SidebarMenuProps) => {
  return (
    <RestyledItem $shouldGrow $isExpanded={false}>
      <nav aria-label={navigationSubject}>{children}</nav>
    </RestyledItem>
  );
};
